import { useCallback, useEffect, useRef, useState } from 'react';
import type { ManifestDetail } from '@/types/manifest';
import { getManifest } from '../api/manifests-api';

interface UseManifestResult {
  manifest: ManifestDetail | null;
  isLoading: boolean;
  isRefetching: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useManifest(id: number | null): UseManifestResult {
  const [manifest, setManifest] = useState<ManifestDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isInitialLoad = useRef(true);
  // Latest-wins guard: each fetch takes a generation number, and only the newest one may write
  // state. Without it a slow 30s poll could land after a post-mutation refetch and put stale
  // barcodes back, or clear isRefetching while the newer request is still running.
  const requestGeneration = useRef(0);

  const fetchManifest = useCallback(async () => {
    const generation = ++requestGeneration.current;
    const isLatest = () => generation === requestGeneration.current;

    if (id === null) {
      setIsLoading(false);
      return;
    }

    if (isInitialLoad.current) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    setError(null);

    try {
      const response = await getManifest(id);
      if (!isLatest()) return;
      if (response.success && response.data) {
        setManifest(response.data);
      } else {
        setError(response.message || 'Failed to fetch manifest');
      }
    } catch {
      if (!isLatest()) return;
      setError('An error occurred while fetching manifest');
    } finally {
      // A superseded request leaves the loading flags to the newest one, which clears them itself.
      if (isLatest()) {
        if (isInitialLoad.current) {
          isInitialLoad.current = false;
          setIsLoading(false);
        } else {
          setIsRefetching(false);
        }
      }
    }
  }, [id]);

  useEffect(() => {
    isInitialLoad.current = true;
    fetchManifest();
  }, [fetchManifest]);

  return {
    manifest,
    isLoading,
    isRefetching,
    error,
    refetch: fetchManifest,
  };
}
